import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const URLParamsContext = createContext({
    params: {},
    setParams: () => { },
});

export const URLParamsProvider = ({ children }) => {
    const [params, setParams] = useState({});
    const [isReady, setIsReady] = useState(false);

    // Load on mount
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const paramObject = {};
        for (const [key, value] of searchParams.entries()) {
            paramObject[key] = value;
        }
        setParams(paramObject);
        setIsReady(true);
    }, []);

    // Function to update URL and state
    const updateUrlParams = useCallback((newParams) => {
        const url = new URL(window.location);

        // Preserve certain fixed parameters (like `page`)
        const preservedKeys = ['page'];
        const preservedParams = {};

        for (const key of preservedKeys) {
            const value = url.searchParams.get(key);
            if (value !== null) {
                preservedParams[key] = value;
            }
        }

        // Clear all existing params
        url.search = '';

        // Apply preserved params first
        Object.entries(preservedParams).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });

        // Then apply new params
        Object.entries(newParams).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                url.searchParams.set(key, value);
            }
        });

        // Update the URL
        window.history.replaceState({}, '', url);

        // Update state
        const updatedParams = {};
        for (const [key, value] of url.searchParams.entries()) {
            updatedParams[key] = value;
        }
        setParams(updatedParams);
    }, []);

    return (
        <URLParamsContext.Provider value={{ params, isReady, updateUrlParams }}>
            {children}
        </URLParamsContext.Provider>
    );
};

export const useURLParams = () => useContext(URLParamsContext);
